/**
 * The paywall funnel's entry point into the quiz.
 *
 * Each screen is a quiz page type, so the funnel inherits what the quiz already
 * does well: a URL per step for drop-off charting, a resumable draft, browser
 * back/forward, and the language switch. `PageRenderer` dispatches here.
 */
import React from 'react';
import { QuizPage, UserProfile } from '../types/quiz';
import { PromoState, verifyPromo } from '../config/paywall';
import { PaywallProfile, usePaywallProfile } from './data';
import { trackPaywall } from './analytics';
import { grantPromo, saveAcceptedPromo } from './session';
import Analyzing from './Analyzing';
import Trust from './Trust';
import Profile from './Profile';
import Scratch from './Scratch';
import PaywallStage from './PaywallStage';

/** Page types this module renders. Keep in sync with `types/quiz.ts`. */
export const PAYWALL_TYPES = [
    'paywall_analyzing',
    'paywall_trust',
    'paywall_profile',
    'paywall_scratch',
    'paywall_offer',
] as const;

export type PaywallPageType = (typeof PAYWALL_TYPES)[number];

export const isPaywallPage = (type: string): type is PaywallPageType =>
    (PAYWALL_TYPES as readonly string[]).includes(type);

const VARIANT = 'paywall-en-v1';

/**
 * 03b/03c wrapper.
 *
 * Asks Polar whether the configured code is a live discount before rendering
 * anything. No live discount means no screen: the step advances silently rather
 * than showing a percentage the checkout would refuse to honour.
 */
const ScratchStep: React.FC<{
    profile: PaywallProfile;
    quizId?: string;
    onNext: () => void;
    onBack?: () => void;
}> = ({ profile, quizId, onNext, onBack }) => {
    const [promo, setPromo] = React.useState<PromoState | null>(null);
    const skipped = React.useRef(false);

    React.useEffect(() => {
        let live = true;

        verifyPromo().then((p) => {
            if (!live) return;
            if (!p.valid) {
                if (skipped.current) return;
                skipped.current = true;
                onNext();

                return;
            }
            setPromo(p);
        });

        return () => {
            live = false;
        };
        // Mount only, deliberately: re-running this because `onNext` was
        // recreated would re-enter the skip path in the middle of a scratch.
    }, []);

    if (!promo) return null;

    const ctx = { quizId, profile, promo, variant: VARIANT };

    return (
        <Scratch
            promo={promo}
            onBack={onBack}
            onReveal={() => {
                // The countdown starts the moment the discount is revealed, and
                // `grantPromo` refuses to move an existing stamp.
                grantPromo();
                trackPaywall('promo_granted', ctx, { percent: promo.percent });
            }}
            onClaim={(p) => {
                saveAcceptedPromo(p.code, p.percent);
                onNext();
            }}
        />
    );
};

export interface PaywallStepProps {
    page: QuizPage;
    userProfile: UserProfile;
    /** Every page the person was shown — what the scoring's headroom is scoped to. */
    pages?: QuizPage[];
    quizId?: string;
    onNext: () => void;
    onBack?: () => void;
}

const PaywallStep: React.FC<PaywallStepProps> = ({ page, userProfile, pages, quizId, onNext, onBack }) => {
    const profile = usePaywallProfile(userProfile, pages);

    switch (page.type as PaywallPageType) {
        case 'paywall_analyzing':
            return <Analyzing onNext={onNext} />;

        case 'paywall_trust':
            return <Trust onNext={onNext} onBack={onBack} />;

        case 'paywall_profile':
            return <Profile profile={profile} onNext={onNext} onBack={onBack} />;

        case 'paywall_scratch':
            return <ScratchStep profile={profile} quizId={quizId} onNext={onNext} onBack={onBack} />;

        case 'paywall_offer':
        default:
            return <PaywallStage profile={profile} userProfile={userProfile} quizId={quizId} />;
    }
};

export default PaywallStep;
